function serviceUrl(name: string, value: string | undefined, fallback: string) {
  const url = (value || fallback).replace(/\/$/, '');
  if (import.meta.env.PROD && /localhost|127\.0\.0\.1|0\.0\.0\.0/i.test(url)) {
    throw new Error(`${name} cannot use a localhost URL in a production build`);
  }
  return url;
}

export const APP_BASE = serviceUrl('VITE_APP_BASE_URL', import.meta.env.VITE_APP_BASE_URL, 'https://munshi-gee.com/app');
export const API_BASE = serviceUrl('VITE_API_BASE_URL', import.meta.env.VITE_API_BASE_URL, 'https://apis.munshi-gee.com');
export const VENDOR_BASE = serviceUrl('VITE_VENDOR_API_BASE_URL', import.meta.env.VITE_VENDOR_API_BASE_URL, API_BASE);
export const SAVING_BASE = serviceUrl('VITE_SAVING_GAME_API_BASE_URL', import.meta.env.VITE_SAVING_GAME_API_BASE_URL, API_BASE);
const WEB_APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';

const TOKEN_KEY = 'munshi_web_auth_token';
const USER_KEY = 'munshi_web_user';
const GUEST_KEY = 'munshi_web_guest_session';
const PENDING_KEY = 'munshi_web_pending_intent';

export type User = {
  id: number;
  full_name: string;
  whatsapp_number?: string;
  is_whatsapp_verified: boolean;
  preferred_language?: 'en' | 'ur';
};

export type PendingIntent = { returnTo: string; action?: string; payload?: unknown };

export const storage = {
  token: () => localStorage.getItem(TOKEN_KEY),
  user: (): User | null => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
  },
  saveAuth: (token: string, user: User) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  clearAuth: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
  guest: () => {
    let value = localStorage.getItem(GUEST_KEY);
    if (!value) {
      value = `web-${crypto.randomUUID()}`;
      localStorage.setItem(GUEST_KEY, value);
    }
    return value;
  },
  pending: (): PendingIntent | null => {
    try { return JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null'); } catch { return null; }
  },
  savePending: (intent: PendingIntent) => sessionStorage.setItem(PENDING_KEY, JSON.stringify(intent)),
  consumePending: () => {
    const value = storage.pending();
    sessionStorage.removeItem(PENDING_KEY);
    return value;
  },
};

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(message: string, status: number, detail?: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

export async function api<T = unknown>(
  path: string,
  options: RequestInit & { base?: string; auth?: boolean } = {},
): Promise<T> {
  const { base = API_BASE, auth = true, ...fetchOptions } = options;
  const headers = new Headers(fetchOptions.headers);
  const token = storage.token();
  if (auth && token) headers.set('Authorization', `Bearer ${token}`);
  if (fetchOptions.body && !(fetchOptions.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  // Keep first-party API activity distinguishable from Android/iOS and direct
  // clients. Do not put user-entered values or persistent user identifiers in
  // these headers.
  if (base === API_BASE) {
    headers.set('X-Munshi-Client-Surface', 'web_app');
    headers.set('X-Munshi-App-Version', WEB_APP_VERSION);
    headers.set('X-Munshi-Request-ID', crypto.randomUUID());
    try {
      const analyticsStatus = sessionStorage.getItem('munshi_web_analytics_status');
      if (analyticsStatus) headers.set('X-Munshi-Analytics-State', analyticsStatus);
    } catch { /* Storage may be unavailable. */ }
  }
  const response = await fetch(`${base}${path}`, { ...fetchOptions, headers });
  const raw = await response.text();
  let data: unknown = null;
  try { data = raw ? JSON.parse(raw) : null; } catch { data = raw; }
  if (!response.ok) {
    if (response.status === 401 && auth) storage.clearAuth();
    const detail = (data as { detail?: unknown })?.detail;
    const message = typeof detail === 'string' ? detail : `Request failed (${response.status})`;
    throw new ApiError(message, response.status, detail);
  }
  return data as T;
}

export const endpoints = {
  login: '/api/v1/auth/login',
  signup: '/api/v1/auth/signup',
  verifyOtp: '/api/v1/auth/verify-otp',
  me: '/api/v1/auth/me',
  preferences: '/api/v1/auth/me/preferences',
  billLookup: '/api/v1/bills/lookup-reference',
  billLookupKe: '/api/v1/bills/lookup-ke-account',
  billExtract: '/api/v1/bills/extract-fields',
  billConfirm: '/api/v1/bills/confirm-and-analyze',
  billHistory: '/api/v1/bills/',
  billSoFarProfiles: '/api/v1/bill-so-far/profiles',
  billSoFarGuestProfile: '/api/v1/bill-so-far/guest/profile',
  billSoFarCalculate: '/api/v1/bill-so-far/calculate',
  billSoFarGuestCalculate: '/api/v1/bill-so-far/guest/calculate',
  appliances: '/api/v1/load/appliances',
  quickLoad: '/api/v1/load/quick-profile',
  solarSize: '/api/v1/solar/size',
  solarRecommend: '/api/v1/solar/recommend',
  solarFinanceMeta: '/api/v1/solar-finance/meta',
  solarFinanceMatch: '/api/v1/solar-finance/match',
  solarFinanceProducts: '/api/v1/solar-finance/products',
  solarFinanceClick: '/api/v1/solar-finance/click',
  vendors: '/api/v1/public/vendors',
  quotes: '/api/v1/solar/quotes',
  billWatches: '/api/v1/notifications/bill-watch',
  loadAlerts: '/api/v1/notifications/load-planner-alerts',
  loadSheddingAreaReportCities: '/api/v1/loadshedding/area-reports/cities',
  loadSheddingAreaReports: '/api/v1/loadshedding/area-reports',
  loadManagementResolve: '/api/v1/load-management/resolve',
  loadManagementSubscriptions: '/api/v1/load-management/subscriptions',
  loadManagementCommunityReport: '/api/v1/load-management/community/report',
};
